// Payload diff preview for the manufacturer Order Intake detail drawer
// (OI-6b). Renders the field-by-field delta between the row's current
// proposed_* / raw_payload and the most recent payload_history entry.
//
// Returns null when there is no history (first submission) so the
// drawer can omit the section entirely.

import { useMemo } from "react";

import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import {
  computeIntakePayloadDiff,
  type IntakeDiffField,
} from "@/lib/orderIntakePayloadDiff";

import { formatIntakeDateTime } from "./ManufacturingOrderIntakeShared";

export type ManufacturingOrderIntakePayloadDiffProps = {
  row: OpsOrderIntakeRow;
};

export function ManufacturingOrderIntakePayloadDiff({
  row,
}: ManufacturingOrderIntakePayloadDiffProps) {
  const diff = useMemo(() => computeIntakePayloadDiff(row), [row]);
  if (!diff) return null;

  const revisedAt = diff.revised_at ? formatIntakeDateTime(diff.revised_at) : null;
  const heading =
    diff.fields.length === 0
      ? "Resubmission applied (no field changes from the prior submission)"
      : `Changes since the prior submission${revisedAt ? ` (revised ${revisedAt})` : ""}`;

  return (
    <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-800 dark:bg-blue-950/35 dark:text-blue-100">
      <div className="font-medium">{heading}</div>
      {diff.prior_hold_reason ? (
        <div className="mt-1 text-blue-900/80 dark:text-blue-100/80">
          Prior hold reason:{" "}
          <span className="italic">&ldquo;{diff.prior_hold_reason}&rdquo;</span>
        </div>
      ) : null}
      {diff.fields.length > 0 ? (
        <ul className="mt-2 space-y-2">
          {diff.fields.map((f) => (
            <DiffEntry key={f.key} field={f} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DiffEntry({ field }: { field: IntakeDiffField }) {
  return (
    <li>
      <div className="font-medium">{field.label}</div>
      <div className="mt-0.5 grid grid-cols-1 gap-x-2 gap-y-0.5 sm:grid-cols-[6rem_minmax(0,1fr)]">
        <div className="text-blue-900/70 dark:text-blue-100/70">Before</div>
        <DiffValue value={field.prior} kind={field.kind} tone="prior" />
        <div className="text-blue-900/70 dark:text-blue-100/70">After</div>
        <DiffValue value={field.current} kind={field.kind} tone="current" />
      </div>
    </li>
  );
}

function DiffValue({
  value,
  kind,
  tone,
}: {
  value: string;
  kind: "scalar" | "json";
  tone: "prior" | "current";
}) {
  const toneClass =
    tone === "prior"
      ? "text-blue-900/80 line-through decoration-blue-400 dark:text-blue-100/70"
      : "font-medium text-blue-900 dark:text-blue-100";
  if (kind === "scalar") {
    return <div className={`break-all ${toneClass}`}>{value}</div>;
  }
  return (
    <pre className={`whitespace-pre-wrap break-words font-mono text-[10px] leading-snug ${toneClass}`}>
      {value}
    </pre>
  );
}
